/**
 * CampaignsPage — `/campaigns`. Таблица боевых кампаний с CPL/статусом.
 *
 * Действия (пауза / ставка / бюджет) НЕ мутируют аккаунт напрямую: они
 * открывают AI-чат с предложением, которое исполняется только после апрува
 * (human-in-the-loop, см. CLAUDE.md). На этапе апрув-гейта (server canUseTool)
 * предложение пройдёт через карточку подтверждения.
 */

import { useMemo, useState } from 'react';
import { usePageHeader } from '@/components/layout/pageHeader';
import { PeriodPicker, type Period } from '@/components/ui/PeriodPicker';
import { Badge, Button, Icon, LoadingBlock, ErrorBlock } from '@/shared/ui';
import { apiFetch } from '@/api/client';
import { useAiStore } from '@/state/aiStore';
import { useAiChat } from '@/hooks/api/useAiChat';
import { useCampaigns, type CampaignRow, type CampaignStatus } from '@/hooks/api/useReports';

interface Recommendation { action: string; impact: string; kind: string }

function rub(value: number | null | undefined): string {
  if (value == null) return '—';
  return new Intl.NumberFormat('ru-RU', { style: 'currency', currency: 'RUB', maximumFractionDigits: 0 }).format(value);
}

const STATUS_TONE: Record<CampaignStatus, string> = {
  ON: 'b-success',
  OFF: 'b-neutral',
  SUSPENDED: 'b-warning',
  ARCHIVED: 'b-neutral',
  UNKNOWN: 'b-neutral',
};

export function CampaignsPage() {
  usePageHeader({ title: 'Кампании', subtitle: 'Боевой аккаунт — действия через апрув' });

  const [period, setPeriod] = useState<Period>({ days: 10 });
  const { data, isLoading, isError, error, refetch } = useCampaigns(period);
  const openPanel = useAiStore((s) => s.openPanel);
  const { sendMessage } = useAiChat();
  const [busy, setBusy] = useState<number | null>(null);

  const rows = useMemo(() => data?.campaigns ?? [], [data]);

  const propose = async (c: CampaignRow, action: string) => {
    setBusy(c.id);
    openPanel();
    await sendMessage(
      `Предложи и подготовь действие «${action}» для кампании «${c.name}» (id ${c.id}). ` +
        `Покажи текущие метрики и обоснуй. Любое изменение — только после моего апрува.`,
    );
    setBusy(null);
  };

  const propPause = (c: CampaignRow) => void propose(c, c.status === 'ON' ? 'поставить на паузу' : 'включить');
  const propBudget = (c: CampaignRow) => void propose(c, 'скорректировать бюджет');

  return (
    <div className="col gap16" style={{ maxWidth: 1200 }}>
      <div className="row spread">
        <span className="muted" style={{ fontSize: 13 }}>Период отчёта</span>
        <PeriodPicker value={period} onChange={setPeriod} />
      </div>

      {isLoading && <LoadingBlock label="Загружаю кампании…" />}
      {isError && <ErrorBlock text={(error as Error)?.message} onRetry={() => void refetch()} />}
      {!isLoading && !isError && data && (
      <section className="card">
        <table className="tbl tbl--compact">
          <thead>
            <tr>
              <th>Кампания</th>
              <th>Домен</th>
              <th>Статус</th>
              <th className="num">Расход</th>
              <th className="num">Заявки</th>
              <th className="num">CPL</th>
              <th style={{ textAlign: 'right' }}>Действия</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((c) => (
              <CampaignTableRow key={c.id} c={c} busy={busy === c.id}
                onPause={() => propPause(c)} onBudget={() => propBudget(c)}
                onApply={(rec) => {
                  openPanel();
                  void sendMessage(`По кампании «${c.name}» (id ${c.id}): ${rec.action}\nПодготовь и обоснуй. Боевой аккаунт — только после апрува.`);
                }} />
            ))}
          </tbody>
        </table>
      </section>
      )}
      <p className="muted" style={{ fontSize: 12.5 }}>
        «Что делать?» — рекомендация нейросети (заземлена доменом/CPL). Применение к боевому аккаунту — только после апрува.
      </p>
    </div>
  );
}

function CampaignTableRow({ c, busy, onPause, onBudget, onApply }: {
  c: CampaignRow;
  busy: boolean;
  onPause: () => void;
  onBudget: () => void;
  onApply: (rec: Recommendation) => void;
}) {
  const [rec, setRec] = useState<Recommendation | null>(null);
  const [loading, setLoading] = useState(false);

  const loadRec = async () => {
    setLoading(true);
    try {
      const detail =
        `Статус ${c.status}, домен ${c.domain}, расход ${Math.round(c.spend)}₽, ` +
        `заявок ${c.leads ?? '—'}, CPL ${c.cpl != null ? Math.round(c.cpl) + '₽' : '—'}.`;
      const r = await apiFetch<Recommendation>('/api/ai/recommend', {
        method: 'POST',
        json: { title: `Кампания «${c.name}»`, detail, campaign: c.name, campaignId: c.id, domain: c.domain },
      });
      setRec(r);
    } finally {
      setLoading(false);
    }
  };

  const dim = c.status === 'ARCHIVED' || c.status === 'OFF';

  return (
    <>
      <tr className={dim ? 'is-dim' : undefined}>
        <td>
          <div className="col" style={{ gap: 2 }}>
            <span style={{ fontWeight: 700 }}>{c.name}</span>
            <span className="muted mono" style={{ fontSize: 11 }}>id {c.id}</span>
          </div>
        </td>
        <td className="mono" style={{ fontSize: 12 }}>{c.domain}</td>
        <td><Badge tone={STATUS_TONE[c.status]}>{c.status}</Badge></td>
        <td className="num">{rub(c.spend)}</td>
        <td className="num">{c.leads == null ? '—' : c.leads}</td>
        <td className="num">{c.tracked ? rub(c.cpl) : '—'}</td>
        <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
          <div className="row" style={{ gap: 6, justifyContent: 'flex-end' }}>
            <Button variant="ai" size="sm" icon="Sparkles" disabled={loading} onClick={() => void loadRec()}>
              {loading ? '…' : 'Что делать?'}
            </Button>
            <Button variant="text" size="sm" disabled={busy} onClick={onPause}>
              {c.status === 'ON' ? 'Пауза' : 'Вкл'}
            </Button>
            <Button variant="text" size="sm" disabled={busy} onClick={onBudget}>Бюджет</Button>
          </div>
        </td>
      </tr>
      {rec && (
        <tr>
          <td colSpan={7} style={{ background: 'var(--surface-alt)' }}>
            <div className="row" style={{ gap: 12, alignItems: 'flex-start' }}>
              <div className="col grow" style={{ gap: 4 }}>
                <span className="row" style={{ gap: 7, fontSize: 13.5 }}>
                  <Icon name="Settings" className="ic" /> <span><b>Что делать:</b> {rec.action}</span>
                </span>
                {rec.impact && (
                  <span className="row muted" style={{ gap: 7, fontSize: 13 }}>
                    <Icon name="Activity" className="ic" /> <span>{rec.impact}</span>
                  </span>
                )}
              </div>
              <Button variant="ai" size="sm" icon="ArrowRight" onClick={() => onApply(rec)}>В чат</Button>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}
